import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import {
  fadeUpVariants,
  scaleFadeVariants,
  ANIMATION_TIMING,
} from "@/lib/animations";

export interface HeroSectionProps {
  /** Background image URL */
  backgroundImage?: string;
  /** Fallback gradient when image fails to load */
  fallbackGradient?: string;
}

/**
 * HeroSection - 首页品牌愿景区域
 *
 * 包含全宽背景图、品牌标语、副标题和主 CTA 按钮
 */
export function HeroSection({
  backgroundImage = "/images/main-image.jpg",
  fallbackGradient = "linear-gradient(180deg, rgba(0,20,40,1) 0%, rgba(0,10,20,1) 50%, rgba(0,5,15,1) 100%)",
}: HeroSectionProps) {
  const { t } = useTranslation("home");
  const prefersReducedMotion = useReducedMotion();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  return (
    <section className="relative h-screen -mt-16 pt-16 flex items-center justify-center overflow-hidden">
      {/* 背景图层 */}
      <div className="absolute inset-0 z-0">
        {/* 渐变 fallback 背景 */}
        <div
          className="absolute inset-0"
          style={{ background: fallbackGradient }}
          aria-hidden="true"
        />

        {/* 背景图片 */}
        {!imageError && (
          <>
            {/* 预加载图片 */}
            <img
              src={backgroundImage}
              alt=""
              className="hidden"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {/* 实际背景 - 使用 object-cover 保持长宽比并填满容器 */}
            <img
              src={backgroundImage}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                imageLoaded ? "opacity-100" : "opacity-0"
              }`}
              aria-hidden="true"
            />
          </>
        )}

        {/* 暗色遮罩层 - 确保文字可读 */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80"
          aria-hidden="true"
        />

        {/* 光线效果 - 模拟水面光线穿透 */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(ellipse at 50% 0%, rgba(100,200,255,0.15) 0%, transparent 60%)",
          }}
          aria-hidden="true"
        />
      </div>

      {/* 内容区域 - 移动端优化间距 */}
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-6 text-center">
        {/* 品牌标语 - 移动端优化字号 */}
        <motion.h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight text-white mb-4 sm:mb-6"
          initial={prefersReducedMotion ? false : "hidden"}
          animate="visible"
          variants={fadeUpVariants}
          custom={ANIMATION_TIMING.HERO_TITLE_DELAY}
        >
          {t("hero.slogan")}
        </motion.h1>

        {/* 副标题 - 移动端优化 */}
        <motion.p
          className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/80 mb-8 sm:mb-12 max-w-4xl mx-auto leading-relaxed whitespace-pre-line"
          initial={prefersReducedMotion ? false : "hidden"}
          animate="visible"
          variants={fadeUpVariants}
          custom={ANIMATION_TIMING.HERO_DESCRIPTION_DELAY}
        >
          {t("hero.subheadline")}
        </motion.p>

        {/* 主 CTA 按钮 - 毛玻璃水晶效果 + Vercel 主题适配 */}
        <motion.div
          initial={prefersReducedMotion ? false : "hidden"}
          animate="visible"
          variants={scaleFadeVariants}
          custom={ANIMATION_TIMING.HERO_CTA_DELAY}
        >
          <Button
            asChild
            variant="outline"
            className="!px-8 sm:!px-10 !py-3 sm:!py-4 h-auto text-base sm:text-lg font-medium rounded-full bg-white/10 backdrop-blur-md border-white/20 text-white hover:bg-white/20 hover:border-white/30 transition-all shadow-2xl"
          >
            <Link to="/assessments" aria-label={t("hero.cta")}>
              {t("hero.cta")}
              <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
            </Link>
          </Button>
        </motion.div>
      </div>

      {/* 底部渐变过渡 - 纯黑到透明，让水景无缝融入 */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-10"
        aria-hidden="true"
      />
    </section>
  );
}

export default HeroSection;
